import { z } from 'zod';

/**
 * Resource request + asset-lifecycle contracts (Phase R.4.1 → R.4.2) — wires the
 * locked IMS Resource Status Lifecycle (operator-cop-doctrine §2.5/§6.4/§9.3) to a
 * real object. R.4.1 shipped the 4-state demo slice (available → requested →
 * assigned → committed); R.4.2 widens to the locked full 7 states (+ checked_in,
 * staging, demobilized — §6.4 decision (a)) and adds the staging-area object.
 *
 * A resource_request is the thing R.3's "Recommend Dispatch" now writes (in
 * addition to the recommendation snapshot) so the lifecycle transitions have an
 * object to attach to. It can link an incident (SITREP v3.1), a hotspot (R.3
 * click-fire), a community (W.1), and/or a staging area (R.4.2), and target either
 * a specific hull (asset_id) or a class (requested_class).
 */

/* ------------------------------------------------------------------ *
 * Enums (lockstep with the migration 0039 CHECK constraints)
 * ------------------------------------------------------------------ */

/** response_assets.lifecycle_state — full 7-state IMS lifecycle (R.4.2 widened
 *  from R.4.1's 4-state demo slice). Array order IS the forward-only progression. */
export const ResourceLifecycleStateSchema = z.enum([
  'available',
  'requested',
  'assigned',
  'checked_in',
  'staging',
  'committed',
  'demobilized',
]);
export type ResourceLifecycleState = z.infer<typeof ResourceLifecycleStateSchema>;

/** The forward-only asset-lifecycle order, shared by the stepper + guards. */
export const RESOURCE_LIFECYCLE_ORDER = [
  'available',
  'requested',
  'assigned',
  'checked_in',
  'staging',
  'committed',
  'demobilized',
] as const satisfies readonly ResourceLifecycleState[];

/** response_assets.readiness. */
export const ResourceReadinessSchema = z.enum(['ready', 'partial', 'unavailable', 'unknown']);
export type ResourceReadiness = z.infer<typeof ResourceReadinessSchema>;

/** response_assets.time_to_deploy_band (Ontario Corps 24–72h surge model). */
export const TimeToDeployBandSchema = z.enum([
  'immediate',
  '24h',
  '48h',
  '72h',
  'on_call',
  'unknown',
]);
export type TimeToDeployBand = z.infer<typeof TimeToDeployBandSchema>;

/** resource_requests.request_status — full 7-state lifecycle (R.4.2) plus the
 *  terminal cancelled/fulfilled. Forward-only through the lifecycle states. */
export const ResourceRequestStatusSchema = z.enum([
  'pending',
  'assigned',
  'checked_in',
  'staging',
  'committed',
  'demobilized',
  'cancelled',
  'fulfilled',
]);
export type ResourceRequestStatus = z.infer<typeof ResourceRequestStatusSchema>;

/** The forward-only request-lifecycle order (cancelled/fulfilled are terminal,
 *  reachable from any open state and NOT part of this progression). Mirrors
 *  RESOURCE_LIFECYCLE_ORDER but in request-status vocabulary (pending == the
 *  asset's 'requested'). */
export const RESOURCE_REQUEST_LIFECYCLE_ORDER = [
  'pending',
  'assigned',
  'checked_in',
  'staging',
  'committed',
  'demobilized',
] as const;

/** resource_requests.urgency. */
export const ResourceUrgencySchema = z.enum(['routine', 'urgent', 'immediate']);
export type ResourceUrgency = z.infer<typeof ResourceUrgencySchema>;

const UUID = z.string().uuid();

/* ------------------------------------------------------------------ *
 * Create — POST /api/resource-requests
 * ------------------------------------------------------------------ */

export const ResourceRequestCreateSchema = z
  .object({
    session_id: z.string().min(1).max(128),
    incident_id: UUID.optional(),
    hotspot_id: UUID.optional(),
    community_id: UUID.optional(),
    /** A specific hull; omit (with requested_class) to request a class. */
    asset_id: UUID.optional(),
    /** Free-text class when asset_id is absent (e.g. "any water bomber"). */
    requested_class: z.string().min(1).max(200).optional(),
    urgency: ResourceUrgencySchema.optional().default('routine'),
    notes: z.string().max(2000).optional(),
  })
  .refine((v) => v.asset_id !== undefined || v.requested_class !== undefined, {
    message: 'either asset_id or requested_class is required',
  });
export type ResourceRequestCreate = z.infer<typeof ResourceRequestCreateSchema>;

/* ------------------------------------------------------------------ *
 * Lifecycle transitions
 * ------------------------------------------------------------------ */

/** PATCH /api/resource-requests/:id/assign — pending → assigned. */
export const ResourceRequestAssignSchema = z.object({
  session_id: z.string().min(1).max(128),
  /** Bind a hull when the request was class-only (optional otherwise). */
  asset_id: UUID.optional(),
});
export type ResourceRequestAssign = z.infer<typeof ResourceRequestAssignSchema>;

/** PATCH /api/resource-requests/:id/check-in — assigned → checked_in (R.4.2). */
export const ResourceRequestCheckInSchema = z.object({
  session_id: z.string().min(1).max(128),
});
export type ResourceRequestCheckIn = z.infer<typeof ResourceRequestCheckInSchema>;

/** PATCH /api/resource-requests/:id/stage — checked_in → staging (R.4.2).
 *  Requires the staging area the resource moves into. */
export const ResourceRequestStageSchema = z.object({
  session_id: z.string().min(1).max(128),
  staging_area_id: UUID,
});
export type ResourceRequestStage = z.infer<typeof ResourceRequestStageSchema>;

/** PATCH /api/resource-requests/:id/commit — assigned|staging → committed.
 *  R.4.1 allowed assigned → committed; R.4.2 also allows staging → committed. */
export const ResourceRequestCommitSchema = z.object({
  session_id: z.string().min(1).max(128),
});
export type ResourceRequestCommit = z.infer<typeof ResourceRequestCommitSchema>;

/** PATCH /api/resource-requests/:id/demobilize — committed → demobilized (R.4.2). */
export const ResourceRequestDemobilizeSchema = z.object({
  session_id: z.string().min(1).max(128),
  demobilization_reason: z.string().max(500).optional(),
});
export type ResourceRequestDemobilize = z.infer<typeof ResourceRequestDemobilizeSchema>;

/** PATCH /api/resource-requests/:id/cancel — cancel from any open state. */
export const ResourceRequestCancelSchema = z.object({
  session_id: z.string().min(1).max(128),
  cancellation_reason: z.string().max(500).optional(),
});
export type ResourceRequestCancel = z.infer<typeof ResourceRequestCancelSchema>;

/* ------------------------------------------------------------------ *
 * Read shapes
 * ------------------------------------------------------------------ */

/** A linked asset's snapshot (joined on detail). */
export const ResourceRequestAssetSchema = z.object({
  id: z.string(),
  name: z.string(),
  asset_class: z.string().nullable(),
  lifecycle_state: z.string(),
  readiness: z.string(),
});

/** The full request, serialized for the wire (GET /:id + POST/PATCH return). */
export const ResourceRequestSchema = z.object({
  id: z.string(),
  request_number: z.string(),
  incident_id: z.string().nullable(),
  incident_number: z.string().nullable(),
  hotspot_id: z.string().nullable(),
  community_id: z.string().nullable(),
  community_name: z.string().nullable(),
  asset_id: z.string().nullable(),
  asset: ResourceRequestAssetSchema.nullable(),
  requested_class: z.string().nullable(),
  request_status: ResourceRequestStatusSchema,
  urgency: ResourceUrgencySchema,
  /** R.4.2 — the staging area this request is staged into (NULL until staged). */
  staging_area_id: z.string().nullable(),
  staging_area_name: z.string().nullable(),
  requested_at: z.string(),
  assigned_at: z.string().nullable(),
  /** R.4.2 timing stamps. */
  checked_in_at: z.string().nullable(),
  staging_at: z.string().nullable(),
  committed_at: z.string().nullable(),
  demobilized_at: z.string().nullable(),
  cancelled_at: z.string().nullable(),
  fulfilled_at: z.string().nullable(),
  cancellation_reason: z.string().nullable(),
  /** R.4.2 — why the resource was demobilized (NULL otherwise). */
  demobilization_reason: z.string().nullable(),
  requested_by_operator_id: z.string().nullable(),
  notes: z.string().nullable(),
});
export type ResourceRequest = z.infer<typeof ResourceRequestSchema>;

/** Compact list row — GET /api/resource-requests. Carries the lifecycle stamps
 *  so the side-panel stepper renders all 7 states without a per-row detail fetch. */
export const ResourceRequestListItemSchema = z.object({
  id: z.string(),
  request_number: z.string(),
  request_status: ResourceRequestStatusSchema,
  urgency: ResourceUrgencySchema,
  incident_id: z.string().nullable(),
  hotspot_id: z.string().nullable(),
  community_id: z.string().nullable(),
  asset_id: z.string().nullable(),
  asset_name: z.string().nullable(),
  requested_class: z.string().nullable(),
  staging_area_id: z.string().nullable(),
  staging_area_name: z.string().nullable(),
  requested_at: z.string(),
  assigned_at: z.string().nullable(),
  checked_in_at: z.string().nullable(),
  staging_at: z.string().nullable(),
  committed_at: z.string().nullable(),
  demobilized_at: z.string().nullable(),
});
export type ResourceRequestListItem = z.infer<typeof ResourceRequestListItemSchema>;

export const ResourceRequestListResponseSchema = z.object({
  requests: z.array(ResourceRequestListItemSchema),
});
export type ResourceRequestListResponse = z.infer<typeof ResourceRequestListResponseSchema>;

/* ------------------------------------------------------------------ *
 * response_assets lifecycle/attribute updates
 * ------------------------------------------------------------------ */

/** PATCH /api/response-assets/:id/lifecycle-state — direct operator transition. */
export const AssetLifecycleStateUpdateSchema = z.object({
  session_id: z.string().min(1).max(128),
  lifecycle_state: ResourceLifecycleStateSchema,
});
export type AssetLifecycleStateUpdate = z.infer<typeof AssetLifecycleStateUpdateSchema>;

/** PATCH /api/response-assets/:id — readiness / time_to_deploy / capability tags. */
export const AssetAttributesUpdateSchema = z
  .object({
    session_id: z.string().min(1).max(128),
    readiness: ResourceReadinessSchema.optional(),
    time_to_deploy_band: TimeToDeployBandSchema.optional(),
    capability_tags: z.array(z.string().min(1).max(60)).max(50).optional(),
  })
  .refine(
    (v) =>
      v.readiness !== undefined ||
      v.time_to_deploy_band !== undefined ||
      v.capability_tags !== undefined,
    { message: 'at least one of readiness / time_to_deploy_band / capability_tags is required' },
  );
export type AssetAttributesUpdate = z.infer<typeof AssetAttributesUpdateSchema>;
